"use client";

import ReviewCard from "../ReviewCard";
import React, { useRef, useEffect, useCallback } from "react";

function MarqueeRow({ reviews, direction = "left", speed = 0.5 }) {
  const trackRef = useRef(null);
  const animationRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const startXRef = useRef(0);
  const startPosRef = useRef(0);
  const currentPosRef = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);

  const isLeft = direction === "left";

  const animate = useCallback(() => {
    const track = trackRef.current;

    if (!track || isDraggingRef.current || isHoveredRef.current) return;

    currentPosRef.current += isLeft ? -speed : speed;
    const maxWidth = track.scrollWidth / 2;

    if (Math.abs(currentPosRef.current) >= maxWidth) {
      currentPosRef.current = 0;
    }

    track.style.transform = `translateX(${currentPosRef.current}px)`;

    animationRef.current = requestAnimationFrame(animate);
  }, [isLeft, speed]);

  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  const handleMouseDown = (e) => {
    const track = trackRef.current;

    if (!track) return;

    isDraggingRef.current = true;
    startXRef.current = e.pageX;
    startPosRef.current = currentPosRef.current;
    lastXRef.current = e.pageX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;

    track.style.transition = "none";

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !trackRef.current) return;

    const deltaX = e.pageX - startXRef.current;
    const newPos = startPosRef.current + deltaX * 1.8;

    currentPosRef.current = newPos;
    trackRef.current.style.transform = `translateX(${newPos}px)`;

    const now = Date.now();
    const dt = now - lastTimeRef.current;

    if (dt > 0) {
      velocityRef.current = ((e.pageX - lastXRef.current) / dt) * 16;
    }

    lastXRef.current = e.pageX;
    lastTimeRef.current = now;
  };

  const handleMouseUp = () => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    const track = trackRef.current;

    if (!track) return;

    let pos = currentPosRef.current;
    const decay = 0.92;

    const applyInertia = () => {
      pos += velocityRef.current;
      velocityRef.current *= decay;

      track.style.transform = `translateX(${pos}px)`;
      currentPosRef.current = pos;

      if (Math.abs(velocityRef.current) > 0.08) {
        animationRef.current = requestAnimationFrame(applyInertia);
      } else {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(applyInertia);
  };

  const handleMouseEnter = () => {
    isHoveredRef.current = true;

    const track = trackRef.current;

    if (track) {
      track.style.transition = "none";
    }
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;

    if (!isDraggingRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  };

  return (
    <div className="tr-scroll-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        ref={trackRef}
        className={`tr-track tr-track-${direction}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor: isDraggingRef.current ? "grabbing" : "grab",
          userSelect: "none",
          willChange: "transform",
        }}
      >
        {reviews.map((review, index) => (
          <ReviewCard key={index} review={review} index={index} />
        ))}
      </div>
    </div>
  );
}

export default MarqueeRow;
